import { useEffect, useState } from 'react';
import { NetworkStatus } from 'apollo-client';
import type { ApolloClient, QueryOptions } from 'apollo-client';
import type { OperationVariables } from '@apollo/react-common';
import type { DocumentNode } from 'graphql';
import { type GraphQLTaggedNode } from 'relay-runtime';

import { fg } from '@confluence/feature-gating';

import { toDocumentNode } from './toDocumentNode';
import type { ApolloErrorInterop } from './ApolloErrorInterop';

type QueryNoFurtherUpdateResult<TData = any> = {
	data: TData | undefined;
	error?: ApolloErrorInterop;
	loading: boolean;
	networkStatus: NetworkStatus;
};

const queryResults = new WeakMap<
	DocumentNode,
	Map<string, Promise<QueryNoFurtherUpdateResult<any>>>
>();

/**
 * This hook works like `useQuery` but it will not update the result after the initial fetch.
 * Essentially it works like fetchPolicy: "standby". See https://www.apollographql.com/docs/react/data/queries/#standby
 *
 * The difference between this hook and `useQuery` is it internally uses pure promise instead of Observable.
 * In the case a query is frequent fetched this hook performs much faster than the Apollo's useQuery.
 * If other fetchPolicy is needed, use `useQuery` instead.
 */
export function useQueryNoFurtherUpdateInterop<TData = any, TVariables = OperationVariables>(
	query: DocumentNode | GraphQLTaggedNode,
	options?: Omit<QueryOptions<TVariables>, 'query' | 'fetchPolicy'> & {
		fetchPolicy?: 'standby';
		skip?: boolean;
		client?: ApolloClient<any>;
	},
): QueryNoFurtherUpdateResult<TData | null> {
	// technically not a hash but it's good enough since variable is an plain object.
	const variablesHash = JSON.stringify(options?.variables || {});
	query = toDocumentNode(query);

	const client = options?.client ?? (window['GLOBAL_APOLLO_CLIENT'] as ApolloClient<any>);
	let dataFromCache: TData | null = null;
	try {
		if (!options?.skip) {
			dataFromCache = client.readQuery<TData, TVariables>({
				query,
				...options,
			});
		}
	} catch {}

	const [result, setResult] = useState<QueryNoFurtherUpdateResult<TData | null>>(() => {
		return dataFromCache
			? {
					data: dataFromCache,
					loading: false,
					networkStatus: NetworkStatus.ready,
				}
			: {
					data: null,
					loading: true,
					networkStatus: NetworkStatus.loading,
				};
	});

	useEffect(() => {
		if (fg('stop_watching_space_level_query')) {
			if (options?.skip) return;

			// If query is previously fetched but with different variables. Re-fetch the query.
			const queryFetched = queryResults.has(query) && queryResults.get(query)!.has(variablesHash);
			if (result.loading || !queryFetched) {
				// Same query + variable should reuse the same promise to avoid going through expensive Apollo client.
				let queryResultPromise: Promise<QueryNoFurtherUpdateResult<TData>>;
				if (queryFetched) {
					queryResultPromise = queryResults.get(query)?.get(variablesHash)!;
				} else {
					queryResultPromise = client.query<TData, TVariables>({
						query,
						...options,
					});
					if (!queryResults.has(query)) {
						queryResults.set(query, new Map());
					}
					queryResults.get(query)!.set(variablesHash, queryResultPromise);
				}

				queryResultPromise
					.then((result) => setResult(result))
					.catch((e): void => {
						setResult({
							...result,
							loading: false,
							networkStatus: NetworkStatus.ready,
							error: e,
						});
					});
			}
		} else {
			if (result.loading && !options?.skip) {
				// Same query + variable should reuse the same promise to avoid going through expensive Apollo client.
				let queryResultPromise: Promise<QueryNoFurtherUpdateResult<TData>>;
				if (queryResults.has(query) && queryResults.get(query)!.has(variablesHash)) {
					queryResultPromise = queryResults.get(query)?.get(variablesHash)!;
				} else {
					queryResultPromise = client.query<TData, TVariables>({
						query,
						...options,
					});
					if (!queryResults.has(query)) {
						queryResults.set(query, new Map());
					}
					queryResults.get(query)!.set(variablesHash, queryResultPromise);
				}

				queryResultPromise
					.then((result) => setResult(result))
					.catch((e): void => {
						setResult({
							...result,
							loading: false,
							networkStatus: NetworkStatus.ready,
							error: e,
						});
					});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [variablesHash]);

	return result;
}
